import plainAxios from "axios";
import axios from "../components/Axios";

// The CDN is private: media under the user's folder needs CloudFront signed cookies. The API signs a policy for the
// user's folder (/cf/get_cookie) and the CDN's /login sets it as cookies on the CDN domain. One request is shared by the
// whole app, refreshed before the policy expires and when the tab comes back, and a CDN image or video that fails is
// reloaded once after a forced refresh.

const KEY = "cdn_cookie"; // sessionStorage {user_id, expires}: this tab set the cookie for user_id, valid until expires (s)
const MARGIN = 1800; // s before expiry to refresh
const FORCE_GAP = 30000; // ms: a forced refresh this soon after the last request reuses it
const RETRY_GAP = 60000; // ms: media failing again this soon after its retry is a real error

let pending = null;
let requestedAt = 0;
let timer = null;
let started = false;
const retried = new WeakMap(); // media element -> { src, at } of its last retry

const now = () => Math.floor(Date.now() / 1000);

function marker(userId) {
  try {
    const m = JSON.parse(sessionStorage.getItem(KEY));
    return m && m.user_id === userId ? m : null;
  } catch (e) {
    return null;
  }
}

export function clearCdnCookie() {
  sessionStorage.removeItem(KEY);
}

function schedule(expires) {
  clearTimeout(timer);
  timer = setTimeout(() => ensureCdnCookie(), Math.max(0, (expires - MARGIN - now()) * 1000));
}

/**
 * Resolves once the logged-in user's CDN cookie is set; never rejects. Concurrent calls share one request.
 * `force` re-signs even when the marker says the cookie is valid (it may be missing or for another user).
 */
export function ensureCdnCookie(force = false) {
  start();
  const userId = localStorage.getItem("user_id");
  if (userId === null) return Promise.resolve();
  if (pending) return pending;
  if (force) {
    if (Date.now() - requestedAt < FORCE_GAP) return Promise.resolve();
  } else {
    const m = marker(userId);
    if (m && m.expires - MARGIN > now()) {
      if (timer === null) schedule(m.expires);
      return Promise.resolve();
    }
  }

  requestedAt = Date.now();
  pending = axios
    .get("/cf/get_cookie")
    .then((response) => {
      const c = response.data;
      // plain axios: the CDN gets no API token, and a CDN error must not trigger the API's 401/403 logout
      return plainAxios
        .get(process.env.REACT_APP_CDN_BASE + "/login", {
          headers: {
            "CloudFront-Key-Pair-Id": c["CloudFront-Key-Pair-Id"],
            "CloudFront-Signature": c["CloudFront-Signature"],
            "CloudFront-Policy": c["CloudFront-Policy"],
          },
          withCredentials: true,
        })
        .then(() => {
          sessionStorage.setItem(KEY, JSON.stringify({ user_id: userId, expires: c["CloudFront-Expires"] }));
          schedule(c["CloudFront-Expires"]);
        });
    })
    .catch((error) => console.log(error))
    .finally(() => {
      pending = null;
    });
  return pending;
}

/** `request` (an API promise whose data holds CDN urls), resolved only once the CDN cookie is set too */
export function withCdnCookie(request) {
  return Promise.all([request, ensureCdnCookie()]).then(([response]) => response);
}

function start() {
  if (started) return;
  started = true;
  // load/error events don't bubble but are seen in the capture phase
  document.addEventListener("error", onMediaError, true);
  document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && ensureCdnCookie());
  window.addEventListener("focus", () => ensureCdnCookie());
}

function onMediaError(e) {
  const el = e.target;
  const media = el.tagName === "SOURCE" ? el.parentElement : el;
  if (!media || !["IMG", "VIDEO", "AUDIO"].includes(media.tagName)) return;
  const src = el.currentSrc || el.src || "";
  if (!src.startsWith(process.env.REACT_APP_CDN_BASE + "/") || localStorage.getItem("user_id") === null) return;

  const last = retried.get(media);
  if (last && last.src === src && Date.now() - last.at < RETRY_GAP) return; // failed again: the page's onError handles it
  retried.set(media, { src, at: Date.now() });
  // hold back the page's onError (hiding the thumbnail, a fallback...) unless the retry fails too
  e.stopPropagation();
  ensureCdnCookie(true).then(() => reload(media));
}

function reload(media) {
  if (media.tagName === "IMG") {
    media.setAttribute("src", media.getAttribute("src")); // setting src, even to the same url, loads it again
    return;
  }
  // a video that failed mid-playback continues where it was
  const time = media.currentTime;
  const playing = !media.paused;
  media.load();
  if (time || playing) {
    media.addEventListener(
      "loadedmetadata",
      () => {
        media.currentTime = time;
        if (playing) media.play().catch(() => {});
      },
      { once: true }
    );
  }
}
